// Router with the prototype's screen ids and QPhone's go/back semantics.
//   go(id, patch)  push the current screen on the history stack, merge `patch` into the nav params, show `id`.
//                  Like QPhone: `toast` is cleared unless the patch sets it; going to W clears `alt` unless set.
//   back(fallback) pop the stack (falls back to `fallback`, or the home screen, when the stack is empty).
//   replace(id, patch) / reset(id, patch) / setParams(patch)
// The URL hash mirrors the screen (#/O8, #/C, #/U2?chap=3; K2 is #/mini) and browser Back works through
// history.pushState. Android's hardware back button calls back() via native.ts.
import { createStore, useStore } from './lib/createStore';
import type { CharId } from '@shared/types';

export const SCREEN_IDS = [
  'O1', 'O2', 'O3', 'O4', 'O5', 'O6', 'O7', 'O8', 'O9', 'O10', 'O11',
  'C', 'T', 'W', 'S', 'R', 'SH',
  'U1', 'U2', 'U3', 'U4',
  'M1', 'M2', 'M3', 'M4', 'M5', 'M6',
  'K1', 'K2',
] as const;
export type ScreenId = (typeof SCREEN_IDS)[number];

export const SCREEN_NAMES: Record<ScreenId, string> = {
  O1: 'Invite', O2: 'What do you most want to change?', O3: 'When do you usually get stuck?', O4: 'How do you like to be talked to?',
  O5: 'How closely should they keep up with you?', O6: "Here's what I've got", O7: 'Who do you want to work beside?',
  O8: 'First help', O9: 'Do it now', O10: 'First day lit', O11: 'Save your progress',
  C: 'Chat', T: 'Today', W: 'Working together', S: 'Stuck', R: 'Card done', SH: 'Share card',
  U1: 'Us', U2: 'Story chapter', U3: 'Your week', U4: 'Your collection',
  M1: 'Me', M2: 'Your buddy', M3: 'Switch buddy', M4: 'VIP', M5: 'Friends', M6: 'Crisis card',
  K1: 'Desktop home', K2: 'Working together (mini window)',
};

export const ONBOARDING: ScreenId[] = ['O1', 'O2', 'O3', 'O4', 'O5', 'O6', 'O7', 'O8', 'O9', 'O10', 'O11'];
export const isOnboarding = (s: ScreenId) => s.charAt(0) === 'O';
/** Screens that show the bottom tab bar (phone layout). */
export const TAB_OF: Partial<Record<ScreenId, 'chat' | 'today' | 'us' | 'me'>> = { C: 'chat', T: 'today', U1: 'us', M1: 'me' };

export type ChatMode = 'first' | 'ask' | 'proposed' | 'planned' | 'changed' | 'evening' | 'n2' | 'n3';

/** Navigation params. They persist across navigations (merged like QPhone's app state) until changed. */
export interface NavParams {
  cur?: string | null;          // card id being worked on (W, S, R)
  alt?: string | null;          // smaller title picked in Stuck; W shows it instead of the card title
  from?: ScreenId | null;       // where S / SH was opened from
  chat?: ChatMode | null;       // chat mode override for C (e.g. 'first' right after onboarding)
  chap?: number | null;         // U2 chapter number
  mode?: string | null;         // screen sub-mode (SH 'card' | 'streak' | 'week', ...)
  rbeat?: number | null;        // R beat to return to from SH
  ch?: CharId | null;           // M3 switch target, etc.
  toast?: string | null;        // one-shot toast shown on arrival (cleared by the next go)
  extra?: Record<string, unknown> | null; // screen-owned extras; key them by screen id: { U3: {...} }
}

interface Entry { s: ScreenId; params: NavParams }
export interface RouterState { s: ScreenId; params: NavParams; hist: Entry[]; depth: number }

const MAX_HIST = 30;
const SS_KEY = 'xm.router';

export const routerStore = createStore<RouterState>({ s: 'O1', params: {}, hist: [], depth: 0 });

// ------------------------------------------------------------------ URL hash <-> screen
const PARAM_KEYS = ['cur', 'chap', 'chat', 'mode', 'from', 'ch'] as const;

export function hashOf(s: ScreenId, params: NavParams = {}): string {
  const path = s === 'K2' ? 'mini' : s;
  const q: string[] = [];
  if (s === 'U2' && params.chap != null) q.push('chap=' + params.chap);
  if ((s === 'W' || s === 'S' || s === 'R') && params.cur) q.push('cur=' + encodeURIComponent(params.cur));
  if (s === 'SH' && params.mode) q.push('mode=' + encodeURIComponent(params.mode));
  return '#/' + path + (q.length ? '?' + q.join('&') : '');
}

export function parseHash(hash: string): Entry | null {
  const m = /^#\/([A-Za-z0-9]+)(?:\?(.*))?$/.exec(hash || '');
  if (!m) return null;
  const id = m[1] === 'mini' ? 'K2' : m[1].toUpperCase();
  if (!(SCREEN_IDS as readonly string[]).includes(id)) return null;
  const params: NavParams = {};
  for (const kv of (m[2] || '').split('&')) {
    const [k, v = ''] = kv.split('=');
    if (!(PARAM_KEYS as readonly string[]).includes(k)) continue;
    const val = decodeURIComponent(v);
    if (k === 'chap') params.chap = Number(val) || null;
    else (params as Record<string, unknown>)[k] = val;
  }
  return { s: id as ScreenId, params };
}

/** Notification / tray routes: 'C', 'T', 'W:<cardId>', 'U2:3' -> screen + params. */
export function parseRoute(route: string): Entry | null {
  const [id, arg] = String(route || '').split(':');
  const s = (id === 'mini' ? 'K2' : id.toUpperCase()) as ScreenId;
  if (!(SCREEN_IDS as readonly string[]).includes(s)) return null;
  const params: NavParams = {};
  if (arg) {
    if (s === 'U2') params.chap = Number(arg) || null;
    else params.cur = arg;
  }
  return { s, params };
}

// ------------------------------------------------------------------ navigation
let homeFor: () => ScreenId = () => 'O1';
/** The app shell tells the router where "home" is (O1 logged out, C or K1 logged in). */
export function setHomeResolver(fn: () => ScreenId) { homeFor = fn; }

const hasWindow = typeof window !== 'undefined';

function persist(st: RouterState) {
  try { sessionStorage.setItem(SS_KEY, JSON.stringify({ s: st.s, params: st.params, hist: st.hist })); } catch { /* private mode */ }
}

function apply(next: RouterState, how: 'push' | 'replace' | 'none') {
  routerStore.replace(next);
  persist(next);
  if (!hasWindow) return;
  const url = location.pathname + location.search + hashOf(next.s, next.params);
  try {
    if (how === 'push') history.pushState({ xmDepth: next.depth }, '', url);
    else if (how === 'replace') history.replaceState({ xmDepth: next.depth }, '', url);
  } catch { /* sandboxed iframes */ }
}

function mergeParams(base: NavParams, to: ScreenId, patch?: NavParams): NavParams {
  const p = Object.assign({}, base, patch || {});
  const has = (k: keyof NavParams) => !!patch && Object.prototype.hasOwnProperty.call(patch, k);
  if (!has('toast')) p.toast = null;
  if (to === 'W' && !has('alt')) p.alt = null;
  return p;
}

export function go(to: ScreenId, patch?: NavParams) {
  const st = routerStore.get();
  const hist = st.hist.concat([{ s: st.s, params: st.params }]).slice(-MAX_HIST);
  apply({ s: to, params: mergeParams(st.params, to, patch), hist, depth: st.depth + 1 }, 'push');
}

export function replace(to: ScreenId, patch?: NavParams) {
  const st = routerStore.get();
  apply({ s: to, params: mergeParams(st.params, to, patch), hist: st.hist, depth: st.depth }, 'replace');
}

/** Clear the history and show `to` (after sign-in, sign-out, finishing onboarding). */
export function reset(to: ScreenId, patch?: NavParams) {
  const st = routerStore.get();
  apply({ s: to, params: mergeParams(patch ? {} : st.params, to, patch), hist: [], depth: st.depth }, 'replace');
}

/** Merge params without navigating (QPhone app.set for nav state). */
export function setParams(patch: NavParams) {
  const st = routerStore.get();
  apply({ ...st, params: Object.assign({}, st.params, patch) }, 'replace');
}

export function canGoBack() { return routerStore.get().hist.length > 0; }

export function back(fallback?: ScreenId) {
  const st = routerStore.get();
  if (!st.hist.length) { replace(fallback || homeFor()); return; }
  // Let the browser pop its entry too when it is ours; popstate finishes the job.
  const hs = hasWindow ? (history.state as { xmDepth?: number } | null) : null;
  if (hs && hs.xmDepth === st.depth && st.depth > 0) { history.back(); return; }
  popTo(st.hist.length - 1);
}

function popTo(len: number) {
  const st = routerStore.get();
  const prev = st.hist[len];
  if (!prev) return;
  apply({ s: prev.s, params: Object.assign({}, prev.params, { toast: null }), hist: st.hist.slice(0, len), depth: Math.max(0, st.depth - (st.hist.length - len)) }, 'none');
}

/** Open a notification/tray route ('W:<cardId>', 'C', ...). */
export function openRoute(route: string) {
  const e = parseRoute(route);
  if (e) go(e.s, e.params);
}

// ------------------------------------------------------------------ boot + browser integration
/** Restore from sessionStorage / the URL hash. Returns the restored screen (or null when the URL has none). */
export function initRouter(): ScreenId | null {
  if (!hasWindow) return null;
  const fromHash = parseHash(location.hash);
  let saved: { s: ScreenId; params: NavParams; hist: Entry[] } | null = null;
  try { saved = JSON.parse(sessionStorage.getItem(SS_KEY) || 'null'); } catch { saved = null; }
  if (fromHash && saved && saved.s === fromHash.s) {
    routerStore.replace({ s: saved.s, params: Object.assign({}, saved.params, fromHash.params), hist: saved.hist || [], depth: 0 });
  } else if (fromHash) {
    routerStore.replace({ s: fromHash.s, params: fromHash.params, hist: [], depth: 0 });
  }
  try { history.replaceState({ xmDepth: 0 }, '', location.href); } catch { /* ignore */ }
  window.addEventListener('popstate', (e) => {
    const st = routerStore.get();
    const d = (e.state as { xmDepth?: number } | null)?.xmDepth;
    if (typeof d === 'number' && d < st.depth) {
      const steps = st.depth - d;
      popTo(Math.max(0, st.hist.length - steps));
      routerStore.set({ depth: d });
      return;
    }
    const h = parseHash(location.hash);
    if (h && (h.s !== st.s)) {
      const hist = st.hist.concat([{ s: st.s, params: st.params }]).slice(-MAX_HIST);
      routerStore.replace({ s: h.s, params: Object.assign({}, st.params, h.params, { toast: null }), hist, depth: typeof d === 'number' ? d : st.depth + 1 });
    }
  });
  return fromHash ? fromHash.s : null;
}

// ------------------------------------------------------------------ hooks
export const useScreen = () => useStore(routerStore, (s) => s.s);
export const useParams = () => useStore(routerStore, (s) => s.params);
export function useRoute() { return useStore(routerStore, (s) => ({ s: s.s, params: s.params })); }
